import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowBackOutlined } from '@mui/icons-material';
import { Alert, Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import { ConfirmDialog, ContentSkeleton, DefinitionList, EditPage, FormTextField, ListRow, PageSections, SectionCard, StatusBadge, ValueBadge } from '../../design-system';
import { useAsyncResource } from '../../shared/useAsyncResource';
import { useUnsavedChangesGuard } from '../../shared/useUnsavedChangesGuard';
import { appReleaseLabel, featureReleaseModeLabels, isEmployeeApp, releaseTargetChanges, type FeatureRelease, type FeatureReleaseMode, type FeatureReleaseUser } from './model';
import type { FeatureReleasePort } from './port';
import { FeatureReleaseUserPicker } from './UserPicker';

function formatDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

function ChangeHistory({ port, featureKey, version, users }: { port: FeatureReleasePort; featureKey: string; version: number; users: Record<string, FeatureReleaseUser> }) {
  const resource = useAsyncResource(() => port.history(featureKey), [port, featureKey, version], '변경 이력을 불러오지 못했습니다.');
  return <Stack spacing={1}>
    {resource.loading ? <ContentSkeleton rows={2} label="변경 이력 확인 중" /> : resource.error ? <Alert severity="error" action={<Button onClick={resource.reload}>다시 시도</Button>}>{resource.error.message}</Alert> : resource.data?.length ? resource.data.map((entry) => <ListRow
      key={entry.id}
      label={<Stack component="span" direction="row" spacing={0.75} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}><ValueBadge label={featureReleaseModeLabels[entry.previousMode]} /><span>→</span><ValueBadge label={featureReleaseModeLabels[entry.mode]} /></Stack>}
      description={<>{formatDateTime(entry.changedAt)} · {users[entry.changedBy]?.displayName || '관리자'}<br />대상 {entry.addedPersonIds.length}명 추가 · {entry.removedPersonIds.length}명 제외</>}
      density="compact"
      inset="none"
    />) : <Typography variant="body2" color="text.secondary">아직 변경 이력이 없습니다.</Typography>}
  </Stack>;
}

export function FeatureReleaseSettings({ feature, port, canManage, onClose, onSaved }: {
  feature: FeatureRelease;
  port: FeatureReleasePort;
  canManage: boolean;
  onClose: () => void;
  onSaved: (feature: FeatureRelease) => void;
}) {
  const employeeApp = isEmployeeApp(feature);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { titleRef.current?.focus(); }, []);
  const canEdit = canManage && employeeApp;
  const [baseline, setBaseline] = useState(feature);
  const [mode, setMode] = useState(feature.mode);
  const [personIds, setPersonIds] = useState(feature.personIds);
  const [users, setUsers] = useState<Record<string, FeatureReleaseUser>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [conflict, setConflict] = useState(false);
  const [notice, setNotice] = useState<string>();
  const rememberUsers = useCallback((items: FeatureReleaseUser[]) => setUsers((current) => ({ ...current, ...Object.fromEntries(items.map((user) => [user.personId, user])) })), []);
  const directory = useAsyncResource(() => port.searchUsers(''), [port], '사용자 정보를 불러오지 못했습니다.');
  useEffect(() => { if (directory.data) rememberUsers(directory.data); }, [directory.data, rememberUsers]);
  const nextPersonIds = mode === 'SELECTED' ? personIds : [];
  const changes = releaseTargetChanges(baseline.personIds, nextPersonIds);
  const dirty = mode !== baseline.mode || changes.added.length > 0 || changes.removed.length > 0;
  useUnsavedChangesGuard({ dirty: canEdit && dirty, busy });
  const targetError = mode !== 'SELECTED' ? undefined : personIds.length === 0
    ? '공개할 사용자를 한 명 이상 선택해 주세요. 아무에게도 제공하지 않으려면 비공개를 선택해 주세요.'
    : personIds.some((id) => users[id]?.eligible === false) ? '사용할 수 없는 계정이 포함되어 있습니다. 대상에서 제외해 주세요.' : undefined;

  const close = () => { if (!busy) { if (dirty) setDiscardOpen(true); else onClose(); } };
  const changeMode = (value: FeatureReleaseMode) => { setMode(value); if (!conflict) setSaveError(undefined); setNotice(undefined); };
  const toggleUser = (user: FeatureReleaseUser) => {
    rememberUsers([user]);
    setPersonIds((current) => current.includes(user.personId) ? current.filter((id) => id !== user.personId) : [...current, user.personId]);
    if (!conflict) setSaveError(undefined);
    setNotice(undefined);
  };
  const save = async () => {
    if (!canEdit || busy || targetError || conflict || !dirty) return;
    setBusy(true);
    setSaveError(undefined);
    try {
      const updated = await port.update(feature.key, { mode, personIds: nextPersonIds, version: baseline.version });
      setBaseline(updated);
      setPersonIds(updated.personIds);
      setMode(updated.mode);
      setConfirmOpen(false);
      onSaved(updated);
    } catch (error) {
      const stale = error instanceof Error && 'status' in error && error.status === 409;
      setConflict(stale);
      setSaveError(stale ? '다른 관리자가 설정을 변경했습니다. 입력한 내용은 유지됩니다. 최신 설정을 불러온 뒤 변경 내용을 다시 확인해 주세요.' : error instanceof Error ? error.message : '저장하지 못했습니다. 다시 시도해 주세요.');
    } finally { setBusy(false); }
  };
  const reloadBaseline = async () => {
    setBusy(true);
    try {
      const latest = (await port.list()).find((item) => item.key === feature.key);
      if (!latest) throw new Error('최신 설정을 찾을 수 없습니다.');
      setBaseline(latest);
      setConflict(false);
      setSaveError(undefined);
      setConfirmOpen(false);
      setNotice('최신 설정을 불러왔습니다. 입력한 내용과 변경 범위를 다시 확인해 주세요.');
    } catch (error) { setSaveError(error instanceof Error ? error.message : '최신 설정을 불러오지 못했습니다.'); }
    finally { setBusy(false); }
  };

  return <>
    <EditPage
      title={`${feature.label} 공개 설정`} titleRef={titleRef} width="wide" presentation="sectioned"
      navigation={<Button startIcon={<ArrowBackOutlined />} onClick={close} disabled={busy}>앱 목록</Button>}
      editActions={canEdit ? <>
        <Button variant="outlined" color="inherit" onClick={close} disabled={busy}>취소</Button>
        <Button variant="fill" disabled={busy || !dirty || Boolean(targetError)} onClick={() => setConfirmOpen(true)}>변경 확인</Button>
      </> : undefined}
    >
      <PageSections>
        {notice && <Alert severity="info">{notice}</Alert>}
        <SectionCard title="앱 정보" bodyPadding="standard" action={<StatusBadge label={appReleaseLabel(baseline)} tone={!employeeApp ? 'neutral' : baseline.mode === 'ALL' ? 'success' : baseline.mode === 'SELECTED' ? 'info' : 'neutral'} />}>
          <Stack spacing={1}>
          <Typography variant="body2">{feature.description}</Typography>
          <DefinitionList items={[
            ['구성원 기능', feature.employeePages.join(' · ') || '구성원 화면 없음'],
            ['관리자 기능', feature.adminPages.join(' · ')],
            ['최종 변경', baseline.updatedAt ? formatDateTime(baseline.updatedAt) : '기본 설정'],
            ...(baseline.updatedBy ? [['변경자', users[baseline.updatedBy]?.displayName || '관리자'] as const] : []),
          ]} />
          </Stack>
        </SectionCard>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.5fr) minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
        <SectionCard title="구성원 공개 범위" help="공개 범위는 이 앱의 구성원 기능에 함께 적용됩니다. 관리자도 자신의 구성원 화면에서는 이 설정을 따르며, 관리자 화면은 기존 관리 권한으로 사용할 수 있습니다. 선택 대상 수는 실제 업무 권한 보유자 수와 다를 수 있습니다." bodyPadding="standard">
        <Stack spacing={2}>
        {!employeeApp && <Alert severity="info">관리자 전용 앱입니다. 구성원 공개 범위를 설정하지 않으며, 기존 관리 권한에 따라 사용할 수 있습니다. 이전 공개 설정과 변경 이력은 보존됩니다.</Alert>}
        {employeeApp && <FormTextField
          label="구성원 공개 범위" select value={mode} disabled={!canEdit || busy}
          onChange={(event) => changeMode(event.target.value as FeatureReleaseMode)}
          help={mode === 'ALL' ? '기존 업무 권한이 있는 모든 구성원에게 공개합니다.' : mode === 'SELECTED' ? '지정한 사용자 중 기존 업무 권한이 있는 사람에게 공개합니다.' : '구성원 화면에서 이 앱의 신규 사용을 제한합니다. 기존 요청 조회·처리는 유지됩니다.'}
        >{Object.entries(featureReleaseModeLabels).map(([value, label]) => <MenuItem key={value} value={value}><ValueBadge label={label} /></MenuItem>)}</FormTextField>}
        {employeeApp && mode === 'SELECTED' && <Stack spacing={1.5}>
          <FeatureReleaseUserPicker port={port} selectedPersonIds={personIds} onSelect={toggleUser} onUsersLoaded={rememberUsers} disabled={!canEdit || busy} error={targetError} />
          <Box>
            <Typography component="h3" variant="subtitle2">선택 대상 {personIds.length}명</Typography>
            {personIds.map((id) => <ListRow key={id}
              label={users[id]?.displayName ?? '선택한 사용자'}
              description={users[id] ? [users[id].workerNumber, users[id].organization].filter(Boolean).join(' · ') : '사용자 정보를 확인할 수 없습니다.'}
              density="compact" inset="none"
              trailing={canEdit && <Button size="small" disabled={busy} aria-label={`${users[id]?.displayName ?? '선택한 사용자'} ${users[id]?.workerNumber ?? ''} 대상에서 제외`} onClick={() => setPersonIds((current) => current.filter((item) => item !== id))}>제외</Button>}
            />)}
          </Box>
        </Stack>}
        </Stack>
        </SectionCard>
        <SectionCard title="변경 이력" bodyPadding="standard">
          <ChangeHistory port={port} featureKey={feature.key} version={baseline.version} users={users} />
        </SectionCard>
        </Box>
      </PageSections>
    </EditPage>
    <ConfirmDialog open={confirmOpen} title="구성원 공개 범위 변경" description={`${feature.label} 앱의 구성원 공개 설정을 변경합니다.`} onCancel={() => setConfirmOpen(false)} onConfirm={save} cancelLabel="계속 수정" confirmLabel="공개 범위 저장" busyLabel="저장 중" busy={busy} confirmDisabled={!canEdit || !dirty || Boolean(targetError) || conflict} error={saveError} errorAction={conflict ? <Button disabled={busy} onClick={() => void reloadBaseline()}>최신 설정 불러오기</Button> : undefined}>
      <DefinitionList items={[
        ['변경 전', <ValueBadge label={featureReleaseModeLabels[baseline.mode]} />], ['변경 후', <ValueBadge label={featureReleaseModeLabels[mode]} />],
        ['선택 대상', mode === 'SELECTED' ? `${nextPersonIds.length}명` : '별도 지정 없음'],
        ['대상 변경', `${changes.added.length}명 추가 · ${changes.removed.length}명 제외`],
      ]} />
      <Alert severity="info">구성원 공개 설정은 업무 권한을 부여하지 않습니다. 관리자 화면과 기존 요청의 조회·승인·반려·취소는 기존 권한에 따라 유지됩니다.</Alert>
    </ConfirmDialog>
    <ConfirmDialog open={discardOpen} intent="discard" title="수정한 내용을 버릴까요?" description="저장하지 않은 공개 범위와 대상 선택이 사라집니다." onCancel={() => setDiscardOpen(false)} onConfirm={onClose} cancelLabel="계속 수정" confirmLabel="버리고 닫기" />
  </>;
}
